import React from 'react';
import { strategyTypeOptions } from '../utils/humanLabels';

export const STRATEGY_SETTINGS_DEFAULTS = {
  bidder_enabled: false,
  heatmap_bidding_enabled: false,
  strategy_type: 'target_cpc',
  target_value: '',
  min_bid: '0.01',
  max_bid: '1000.00',
  stock_threshold: '',
};

/** Преобразует ответ GET /bidder/campaign/:id/settings в значения формы. */
export function strategySettingsFromApi(data) {
  const strategyType = data.strategy_type || 'target_cpc';
  let targetValue = '';
  if (strategyType === 'target_cpc' || strategyType === 'cpc') {
    targetValue = data.target_cpc ? String(data.target_cpc) : '';
  } else if (strategyType === 'conversion_cost' || strategyType === 'cpo' || strategyType === 'threshold_scout') {
    targetValue = data.target_conversion_cost ? String(data.target_conversion_cost) : '';
  } else if (strategyType === 'drr') {
    targetValue = data.target_drr ? String(data.target_drr) : '';
  }

  return {
    bidder_enabled: Boolean(data.bidder_enabled),
    heatmap_bidding_enabled: Boolean(data.heatmap_bidding_enabled),
    strategy_type: strategyType,
    target_value: targetValue,
    min_bid: data.min_bid ? String(data.min_bid) : '0.01',
    max_bid: data.max_bid ? String(data.max_bid) : '1000.00',
    stock_threshold: data.stock_threshold == null ? '' : String(data.stock_threshold),
    scout_status: data.scout_status || null,
  };
}

/** Тело PUT /bidder/campaign/:id/settings из значений формы. */
export function strategySettingsToPayload(values, campaignId) {
  return {
    campaign_id: campaignId,
    bidder_enabled: values.bidder_enabled,
    heatmap_bidding_enabled: values.heatmap_bidding_enabled,
    strategy_type: values.strategy_type,
    target_value: values.target_value || null,
    min_bid: values.min_bid,
    max_bid: values.max_bid,
    stock_threshold: values.stock_threshold === '' ? null : Number(values.stock_threshold),
  };
}
function field(values, onChange, key, val) {
  onChange({ ...values, [key]: val });
}

function StrategyToggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-700">{label}</div>
        {description ? (
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">{description}</p>
        ) : null}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`shrink-0 w-11 h-6 rounded-full relative transition-colors ${
          checked ? 'bg-indigo-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
            checked ? 'right-0.5' : 'left-0.5'
          }`}
        />
      </button>
    </div>
  );
}

function ScoutProbeStats({ scoutStatus }) {
  if (!scoutStatus) return null;

  const hourly = scoutStatus.hourly_history || [];
  const rungs = scoutStatus.rungs || [];
  const history = scoutStatus.probe_history || [];
  const tableRows = rungs.length
    ? rungs
    : [...(hourly.length ? hourly : history).reduce((acc, p) => {
      const bid = p.bid ?? p.applied_bid ?? p.planned_bid;
      if (bid == null) return acc;
      const cur = acc.get(bid) || { bid, spend: 0, orders: 0, clicks: 0, slots: 0 };
      cur.spend += Number(p.spend) || 0;
      cur.orders += Number(p.orders) || 0;
      cur.clicks += Number(p.clicks) || 0;
      cur.slots += 1;
      acc.set(bid, cur);
      return acc;
    }, new Map()).values()].sort((a, b) => a.bid - b.bid);

  const formatHour = (dateStr, hour) => {
    if (!dateStr && dateStr !== 0) return '—';
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return `${dateStr} ${hour}:00`;
    return `${m[3]}.${m[2]} ${String(hour).padStart(2, '0')}:00`;
  };

  const formatSlot = (slot) => {
    if (!slot) return '—';
    const m = String(slot).match(/^(\d{4}-\d{2}-\d{2})_(\d+)$/);
    if (!m) return slot;
    return formatHour(m[1], m[2]);
  };

  // Показываем последние часы снизу вверх удобнее — новые сверху
  const hourlyDesc = [...hourly].reverse().slice(0, 72);

  if (!tableRows.length && !hourly.length && !history.length && scoutStatus.wb_min_bid == null) {
    return null;
  }

  return (
    <div className="mt-3 space-y-3">
      {scoutStatus.wb_min_bid != null ? (
        <p className="text-xs text-gray-600">
          Мин. ставка WB: <strong>{scoutStatus.wb_min_bid} ₽</strong>
          {scoutStatus.effective_min_bid != null && scoutStatus.effective_min_bid !== scoutStatus.wb_min_bid
            ? ` · нижняя граница проб: ${scoutStatus.effective_min_bid} ₽`
            : ''}
          {scoutStatus.effective_max_bid != null
            ? ` · верх: ${scoutStatus.effective_max_bid} ₽`
            : ''}
        </p>
      ) : null}

      {hourlyDesc.length > 0 ? (
        <div>
          <p className="text-xs text-gray-600 mb-1">
            По часам (ставка на час · как тепловая карта)
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200/80 max-h-64 overflow-y-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Час</th>
                  <th className="px-2 py-1.5 text-right font-medium">Ставка</th>
                  <th className="px-2 py-1.5 text-right font-medium">Расход</th>
                  <th className="px-2 py-1.5 text-right font-medium">Заказы</th>
                  <th className="px-2 py-1.5 text-right font-medium">CPO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hourlyDesc.map((p) => {
                  const cpo = Number(p.orders) > 0
                    ? Math.round(Number(p.spend) / Number(p.orders))
                    : null;
                  return (
                    <tr key={`${p.stat_date}_${p.stat_hour}`} className="text-gray-800">
                      <td className="px-2 py-1.5">{formatHour(p.stat_date, p.stat_hour)}</td>
                      <td className="px-2 py-1.5 text-right font-medium">{p.bid != null ? p.bid : '—'}</td>
                      <td className="px-2 py-1.5 text-right">{Number(p.spend || 0).toFixed(0)}</td>
                      <td className="px-2 py-1.5 text-right">{p.orders || 0}</td>
                      <td className="px-2 py-1.5 text-right">{cpo != null ? cpo : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : history.length > 0 ? (
        <div>
          <p className="text-xs text-gray-600 mb-1">История проб по слотам</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200/80">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Слот</th>
                  <th className="px-2 py-1.5 text-right font-medium">Ставка</th>
                  <th className="px-2 py-1.5 text-right font-medium">Расход</th>
                  <th className="px-2 py-1.5 text-right font-medium">Заказы</th>
                  <th className="px-2 py-1.5 text-right font-medium">CPO</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {history.map((p, idx) => {
                  const bid = p.applied_bid ?? p.planned_bid;
                  const cpo = Number(p.orders) > 0
                    ? Math.round(Number(p.spend) / Number(p.orders))
                    : null;
                  return (
                    <tr key={`${p.slot}-${bid}-${idx}`} className="text-gray-800">
                      <td className="px-2 py-1.5">{formatSlot(p.slot)}</td>
                      <td className="px-2 py-1.5 text-right font-medium">{bid != null ? bid : '—'}</td>
                      <td className="px-2 py-1.5 text-right">{Number(p.spend || 0).toFixed(0)}</td>
                      <td className="px-2 py-1.5 text-right">{p.orders || 0}</td>
                      <td className="px-2 py-1.5 text-right">{cpo != null ? cpo : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          Почасовая статистика появится после ближайшего сбора (:45 / :55 МСК).
        </p>
      )}

      {tableRows.length > 0 ? (
        <div>
          <p className="text-xs text-gray-600 mb-1">Сводка по ставкам (все часы обучения)</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200/80">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Ставка ₽</th>
                  <th className="px-2 py-1.5 text-right font-medium">Расход</th>
                  <th className="px-2 py-1.5 text-right font-medium">Заказы</th>
                  <th className="px-2 py-1.5 text-right font-medium">CPO</th>
                  <th className="px-2 py-1.5 text-right font-medium">Часов</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tableRows.map((r) => {
                  const cpo = Number(r.orders) > 0
                    ? Math.round(Number(r.spend) / Number(r.orders))
                    : null;
                  return (
                    <tr key={r.bid} className="text-gray-800">
                      <td className="px-2 py-1.5 font-medium">{r.bid}</td>
                      <td className="px-2 py-1.5 text-right">{Number(r.spend || 0).toFixed(0)}</td>
                      <td className="px-2 py-1.5 text-right">{r.orders || 0}</td>
                      <td className="px-2 py-1.5 text-right">{cpo != null ? cpo : '—'}</td>
                      <td className="px-2 py-1.5 text-right">{r.slots || 1}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Единая форма настроек стратегии биддера (одна кампания и массовое редактирование).
 * @param {{ values: object, onChange: (next: object) => void, bulkMode?: boolean }} props
 */
export default function StrategySettingsForm({ values, onChange, bulkMode = false }) {
  const set = (key, val) => field(values, onChange, key, val);

  const heatmapDescription = bulkMode
    ? 'Применится ко всем выбранным кампаниям. Корректировка ставки каждый час в :00 МСК.'
    : 'Корректировка ставки каждый час в :00 МСК: выше в часы с большим числом заказов и более низким CPO';

  return (
    <div className="space-y-4">
      <StrategyToggle
        checked={values.bidder_enabled}
        onChange={(v) => set('bidder_enabled', v)}
        label="Включить биддер"
      />
      <StrategyToggle
        checked={values.heatmap_bidding_enabled}
        onChange={(v) => set('heatmap_bidding_enabled', v)}
        label="Биддинг по тепловой карте"
        description={heatmapDescription}
      />
      <div>
        <label className="block mb-1 text-gray-700">Тип стратегии</label>
        <select
          value={values.strategy_type}
          onChange={(e) => set('strategy_type', e.target.value)}
          className="glass-input w-full"
        >
          {strategyTypeOptions
            .filter((o) => o.value !== 'drr')
            .map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
        </select>
        {values.strategy_type === 'threshold_scout' ? (
          <p className="text-xs text-gray-500 mt-1 leading-snug">
            8 дней обучение: ставки прыгают между мин. и макс. (например 300 → 60 → 200 ₽)
            в слоты 9:00, 14:00 и 19:00 МСК, без плавного шага. Затем фиксируется порог
            с лучшим CPO относительно цели. Тепловая карта в это время не двигает ставку.
          </p>
        ) : null}
        {values.strategy_type === 'threshold_scout' && values.scout_status?.phase === 'learning' ? (
          <p className="text-xs text-indigo-700 mt-1">
            Обучение: день {values.scout_status.day} из {values.scout_status.learn_days}
            {values.scout_status.current_bid
              ? ` · сейчас проба ${values.scout_status.current_bid} ₽`
              : ''}
            {values.scout_status.wb_min_bid != null
              ? ` · мин WB ${values.scout_status.wb_min_bid} ₽`
              : ''}
            {values.scout_status.effective_max_bid != null
              ? ` · макс ${values.scout_status.effective_max_bid} ₽`
              : ''}
            {values.scout_status.leader_bid != null
              ? ` (лидер ${values.scout_status.leader_bid} ₽ +10%)`
              : ''}
          </p>
        ) : null}
        {values.strategy_type === 'threshold_scout' ? (
          <ScoutProbeStats scoutStatus={values.scout_status} />
        ) : null}
        {values.strategy_type === 'threshold_scout' && values.scout_status?.phase === 'exploit' ? (
          <p className="text-xs text-indigo-700 mt-1">
            Найденный порог: {values.scout_status.best_bid} ₽
            {values.scout_status.best_cpo != null
              ? ` · CPO ~${Math.round(values.scout_status.best_cpo)} ₽`
              : ''}
          </p>
        ) : null}
      </div>
      <div>
        <label className="block mb-1 text-gray-700">Целевое значение</label>
        <input
          type="number"
          value={values.target_value}
          onChange={(e) => set('target_value', e.target.value)}
          className="glass-input w-full"
          step="0.01"
          placeholder={
            values.strategy_type === 'conversion_cost' || values.strategy_type === 'threshold_scout'
              ? 'CPO, ₽'
              : 'CPC, ₽'
          }
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block mb-1 text-gray-700">Мин. ставка</label>
          <input
            type="number"
            value={values.min_bid}
            onChange={(e) => set('min_bid', e.target.value)}
            className="glass-input w-full"
            step="0.01"
          />
        </div>
        <div>
          <label className="block mb-1 text-gray-700">Макс. ставка</label>
          <input
            type="number"
            value={values.max_bid}
            onChange={(e) => set('max_bid', e.target.value)}
            className="glass-input w-full"
            step="0.01"
          />
        </div>
      </div>
      <div>
        <label className="block mb-1 text-gray-700">Порог остатка, шт</label>
        <input
          type="number"
          value={values.stock_threshold}
          onChange={(e) => set('stock_threshold', e.target.value)}
          className="glass-input w-full"
          min="0"
          step="1"
          placeholder="по умолчанию 10"
        />
        <p className="text-xs text-gray-500 mt-1">
          Если остаток продвигаемого товара ниже этого значения — ставка плавно снижается
          (при 0 шт кампания не участвует). Пусто — порог 10 шт. Введите 0, чтобы отключить
          снижение по остатку.
        </p>
      </div>
    </div>
  );
}
